import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { InterviewerPicker } from "@/components/interviewer-picker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useInterviewerAssignment } from "@/hooks/use-interviewer-assignment";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

interface ChangeInterviewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interviewId: string;
  interviewStageId: string;
  applicationId: string;
  jobOpeningId: string;
  hasCalendarEvent: boolean;
  currentInterviewerId: string;
  currentInterviewerGroupId: string | null;
  durationMinutes?: number;
}

export function ChangeInterviewerDialog({
  open,
  onOpenChange,
  interviewId,
  interviewStageId,
  applicationId,
  jobOpeningId,
  hasCalendarEvent,
  currentInterviewerId,
  currentInterviewerGroupId,
  durationMinutes = 60,
}: ChangeInterviewerDialogProps) {
  const { toast } = useToast();

  const assignment = useInterviewerAssignment({
    jobOpeningId,
    stageId: interviewStageId,
    enabled: open,
    excludeInterviewerId: currentInterviewerId,
    excludeGroupId: currentInterviewerGroupId,
  });

  const [mode, setMode] = useState<"cancel-reschedule" | "swap-only">("swap-only");
  const [sendEmail, setSendEmail] = useState(true);

  useEffect(() => {
    if (open) {
      setMode("swap-only");
      setSendEmail(true);
    }
  }, [open]);

  // Whether email controls should be visible
  const showEmailOption = hasCalendarEvent ? mode === "cancel-reschedule" : true;

  const mutation = useMutation({
    mutationFn: async () => {
      const isCancelReschedule = hasCalendarEvent && mode === "cancel-reschedule";
      return rpcClient.interviews.changeInterviewer({
        id: interviewId,
        interviewerId: assignment.selectedGroupId ? undefined : assignment.interviewerId,
        interviewerGroupId: assignment.selectedGroupId || undefined,
        cancelExistingBooking: isCancelReschedule,
        sendEmail: showEmailOption ? sendEmail : false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.interviews.getByApplication.queryOptions({ input: { applicationId } })
          .queryKey,
      });
      toast({
        title: "Entrevistador trocado",
        description: "O entrevistador foi atualizado.",
      });
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível trocar o entrevistador.",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trocar entrevistador</DialogTitle>
          <DialogDescription>Selecione o novo entrevistador para esta etapa.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <InterviewerPicker
            assignmentOptions={assignment.assignmentOptions}
            people={assignment.people}
            isLoading={assignment.isLoading}
            interviewerId={assignment.interviewerId}
            setInterviewerId={assignment.setInterviewerId}
            selectedGroupId={assignment.selectedGroupId}
            setSelectedGroupId={assignment.setSelectedGroupId}
            assignmentMode={assignment.assignmentMode}
            setAssignmentMode={assignment.setAssignmentMode}
            durationMinutes={durationMinutes}
            showCopyLink
            showSendEmail={showEmailOption}
            sendEmail={sendEmail}
            onSendEmailChange={setSendEmail}
          />

          {/* Calendar event options */}
          {hasCalendarEvent && (
            <RadioGroup
              value={mode}
              onValueChange={(val) => setMode(val as "cancel-reschedule" | "swap-only")}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="cancel-reschedule" id="cancel-reschedule" />
                <div className="space-y-1">
                  <Label htmlFor="cancel-reschedule" className="font-medium">
                    Cancelar entrevista atual e reagendar
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    O evento no Google Calendar será cancelado
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="swap-only" id="swap-only" />
                <div className="space-y-1">
                  <Label htmlFor="swap-only" className="font-medium">
                    Apenas trocar entrevistador
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    O entrevistador será trocado sem cancelar o agendamento existente
                  </p>
                </div>
              </div>
            </RadioGroup>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!assignment.hasSelection || mutation.isPending}
          >
            {mutation.isPending ? "Trocando..." : "Confirmar troca"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
